export interface AstrologerProfile {
  id: string;
  user_id: string;
  professional_name: string;
  title: string | null;
  bio: string | null;
  photo_url: string | null;
  specialties: string[] | null;
  years_experience: number | null;
  certifications: string[] | null;
  show_phone: boolean;
  phone: string | null;
  show_whatsapp: boolean;
  whatsapp: string | null;
  show_email: boolean;
  contact_email: string | null;
  website: string | null;
  instagram: string | null;
  tiktok: string | null;
  slug: string | null;
  is_public: boolean;
  doc_header_type: 'name_only' | 'name_and_contact' | 'full_header' | 'custom_message' | 'logo_only' | 'logo_and_contact';
  doc_logo_url: string | null;
  doc_primary_color: string;
  doc_accent_color: string;
  doc_font: string;
  doc_custom_message: string | null;
  doc_footer_text: string | null;
  doc_include_disclaimer: boolean;
  doc_disclaimer_text: string | null;
  is_active: boolean;
  suspended_at: string | null;
  suspended_reason: string | null;
  total_clients: number;
  total_analyses: number;
  wizard_completed: boolean;
  created_at: string;
  updated_at: string;
}

export interface AstrologerClient {
  id: string;
  astrologer_id: string;
  name: string;
  email: string | null;
  phone: string | null;
  whatsapp: string | null;
  birth_date: string;
  birth_time: string | null;
  birth_time_unknown: boolean;
  birth_city: string | null;
  birth_lat: number | null;
  birth_lng: number | null;
  birth_timezone: string;
  gender: string | null;
  notes: string | null;
  natal_chart_json: NatalChartJson | null;
  natal_chart_calculated_at: string | null;
  natal_chart_hash: string | null;
  tags: string[] | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface NatalChartJson {
  sun_sign: string;
  moon_sign: string;
  mercury_sign: string;
  venus_sign: string;
  mars_sign: string;
  ascendant: string | null;
  planets?: { name: string; sign: string; longitude: number }[];
}

export interface AstrologerAnalysis {
  id: string;
  astrologer_id: string;
  client_id: string;
  service_slug: string;
  service_name: string;
  client2_id: string | null;
  status: 'pending' | 'processing' | 'completed' | 'error';
  content_json: Record<string, unknown> | null;
  error_message: string | null;
  doc_url: string | null;
  doc_generated_at: string | null;
  doc_downloads: number;
  tokens_used: number;
  model_used: string | null;
  generation_time_ms: number | null;
  natal_chart_hash: string | null;
  generated_by_admin: boolean;
  created_at: string;
  updated_at: string;
}

export interface AstrologerNote {
  id: string;
  astrologer_id: string;
  client_id: string;
  content: string;
  created_at: string;
}

// Service options available in the professional module
export interface ProServiceOption {
  slug: string;
  name: string;
  description: string;
  category: string;
  needsSecondClient?: boolean;
  needsWeekNumber?: boolean;
  needsPeriod?: boolean;
  cardOptions?: number[];
  needsQuestion?: boolean;
  needsPetInfo?: boolean;
  needsCity?: boolean;
  noCache?: boolean;
}

export const PRO_SERVICES: ProServiceOption[] = [
  // Análise Natal
  { slug: 'mapa-astral',      name: 'Mapa Astral Completo',   description: 'Análise completa do mapa natal com todos os planetas, casas e aspectos.',         category: 'Análise Natal' },
  { slug: 'start-astral',     name: 'Start Astral',           description: 'Introdução ao mapa natal com os pontos essenciais, ideal para iniciantes.',       category: 'Análise Natal' },
  { slug: 'vedic-astrology',  name: 'Astrologia Védica',      description: 'Mapa astral segundo a tradição védica indiana (Jyotish) com lagnas e nakshatras.', category: 'Análise Natal' },
  { slug: 'human-design',     name: 'Design Humano',          description: 'Perfil de Design Humano baseado no horário e local de nascimento.',                 category: 'Análise Natal' },
  { slug: 'astrocartografia', name: 'Astrocartografia',       description: 'Análise de onde no mundo os planetas são mais favoráveis para o cliente.',         category: 'Análise Natal', needsCity: true },
  // Previsões
  { slug: 'previsao',         name: 'Previsão Personalizada', description: 'Previsão astrológica personalizada para o período selecionado.',                   category: 'Previsões', needsPeriod: true },
  // Relacionamentos
  { slug: 'synastry',         name: 'Sinastria',              description: 'Análise de compatibilidade entre dois mapas natais.',                              category: 'Relacionamentos', needsSecondClient: true },
  { slug: 'astromatch',       name: 'AstroMatch',             description: 'Sinastria focada em conexões românticas e compatibilidade afetiva.',               category: 'Relacionamentos', needsSecondClient: true },
  // Oráculos
  { slug: 'tarot',            name: 'Tarô de Marselha',       description: 'Leitura de Tarô de Marselha com interpretação simbólica e astrológica.',           category: 'Oráculos', cardOptions: [1, 3, 7, 10], noCache: true },
  { slug: 'lenormand',        name: 'Baralho Cigano',         description: 'Leitura do Baralho Cigano (Lenormand) com mensagens práticas e intuitivas.',       category: 'Oráculos', cardOptions: [1, 3, 5, 9],  noCache: true },
  { slug: 'i-ching',          name: 'I Ching',                description: 'Oráculo chinês milenar com hexagramas e mensagens do Yi Jing.',                   category: 'Oráculos', noCache: true },
  { slug: 'questions',        name: 'Pergunte aos Astros',    description: 'Respostas direcionadas a perguntas específicas a partir do mapa natal.',            category: 'Oráculos', needsQuestion: true, noCache: true },
  // Jornadas
  { slug: 'astro-coach',      name: 'Astro Coach',            description: 'Programa de coaching semanal baseado nos ciclos planetários do cliente.',          category: 'Jornadas', needsWeekNumber: true },
  { slug: 'astro-pet',        name: 'Astro Pet',              description: 'Análise astrológica do mapa do pet do cliente.',                                   category: 'Jornadas', needsPetInfo: true, noCache: true },
  // Números e Ciclos
  { slug: 'numerologia',      name: 'Numerologia Completa',   description: 'Análise numerológica completa baseada no nome e data de nascimento.',              category: 'Números e Ciclos' },
  { slug: 'eclipses',         name: 'Eclipse Insight',        description: 'Impacto dos eclipses no mapa natal e previsão de ciclos de transformação.',        category: 'Números e Ciclos' },
  { slug: 'retrogrado',       name: 'Radar Retrógrado',       description: 'Análise do impacto dos planetas retrógrados no mapa natal do cliente.',            category: 'Números e Ciclos' },
];

// Map from PRO_SERVICES slug → nav_menu_config item ID (controls global on/off)
export const SERVICE_SLUG_TO_NAV_ID: Record<string, string> = {
  'mapa-astral':      'birth-chart',
  'start-astral':     'birth-chart',
  'vedic-astrology':  'vedic-astrology',
  'human-design':     'human-design',
  'astrocartografia': 'astrocartografia',
  'previsao':         'forecast',
  'synastry':         'synastry',
  'astromatch':       'astromatch',
  'tarot':            'tarot',
  'lenormand':        'lenormand',
  'i-ching':          'i-ching',
  'questions':        'questions',
  'astro-coach':      'astro-coach',
  'astro-pet':        'astro-pet',
  'numerologia':      'numerology',
  'eclipses':         'eclipses',
  'retrogrado':       'retrogrado',
};

// Relationship between two clients
export type RelationshipType = 'conjuge' | 'parceiro' | 'pai_mae' | 'filho_filha' | 'irmao_irma' | 'amigo' | 'outro';

export const RELATIONSHIP_LABELS: Record<RelationshipType, string> = {
  conjuge:      'Cônjuge',
  parceiro:     'Parceiro(a)',
  pai_mae:      'Pai / Mãe',
  filho_filha:  'Filho(a)',
  irmao_irma:   'Irmão(ã)',
  amigo:        'Amigo(a)',
  outro:        'Outro',
};

export interface AstrologerClientRelationship {
  id:                string;
  astrologer_id:     string;
  client_a_id:       string;
  client_b_id:       string;
  relationship_type: RelationshipType;
  notes:             string | null;
  created_at:        string;
  updated_at:        string;
  // Joined
  related_client?: { id: string; name: string; birth_date: string };
}

// Feedback submitted by astrologers to Professya admin
export type FeedbackCategory = 'bug' | 'sugestao_funcionalidade' | 'melhoria_servico' | 'ux' | 'novo_servico' | 'outro';
export type FeedbackStatus   = 'pendente' | 'em_analise' | 'planejado' | 'em_desenvolvimento' | 'concluido' | 'nao_implementado';

export const FEEDBACK_CATEGORY_LABELS: Record<FeedbackCategory, string> = {
  bug:                    '🐛 Bug / Erro Técnico',
  sugestao_funcionalidade:'✨ Sugestão de Funcionalidade',
  melhoria_servico:       '⚡ Melhoria em Serviço',
  ux:                     '🖥 Problema de Usabilidade',
  novo_servico:           '🆕 Novo Serviço',
  outro:                  '💬 Outro',
};

export const FEEDBACK_STATUS_LABELS: Record<FeedbackStatus, { label: string; color: string }> = {
  pendente:           { label: 'Pendente',         color: 'var(--text-muted)' },
  em_analise:         { label: 'Em análise',        color: '#fbbf24' },
  planejado:          { label: 'Planejado',         color: '#60a5fa' },
  em_desenvolvimento: { label: 'Em desenvolvimento', color: '#a78bfa' },
  concluido:          { label: '✅ Concluído',      color: '#4ade80' },
  nao_implementado:   { label: 'Não implementado',  color: '#f87171' },
};

export interface AstrologerFeedback {
  id:            string;
  astrologer_id: string;
  category:      FeedbackCategory;
  subject:       string;
  description:   string;
  service_slug:  string | null;
  status:        FeedbackStatus;
  admin_notes:   string | null;
  created_at:    string;
  updated_at:    string;
}
